import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, Paper, Slide, Typography } from "@mui/material";
import { X } from "react-feather";
import { useTranslation } from "react-i18next";
import { isNewBuildAvailable } from "src/_utils/appVersion";

/*
 * "A new version is available" banner.
 *
 * Companion to ChunkLoadErrorBoundary: that one recovers when stale code
 * CRASHES (a chunk 404s after a deploy); this one handles the quieter case where
 * stale code keeps WORKING — typically an installed PWA that was resumed from
 * the home screen, or a dashboard tab left open for days — so the user never
 * gets a bug fix until they happen to reload. See src/_utils/appVersion.js.
 *
 * When to check: on boot, every time the page becomes visible again (that is
 * the PWA "resume" signal on both Android and iOS), on `pageshow` (bfcache
 * restore), and on a slow interval for tabs that never lose focus. Checks are
 * throttled so a user flicking between apps does not hammer the manifest.
 *
 * Reload is user-initiated on purpose: an automatic reload on resume would
 * throw away whatever the merchant was typing (an order, a task, step 2 of
 * registration). Dismissing only hides the banner until the next check finds
 * the build still stale — staying on old code indefinitely is the thing this
 * exists to prevent.
 */

const MIN_CHECK_GAP_MS = 60 * 1000;
const POLL_INTERVAL_MS = 15 * 60 * 1000;

const AppUpdatePrompt = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const lastCheckedAt = useRef(0);

  const check = useCallback(async ({ force = false } = {}) => {
    if (document.visibilityState !== "visible") return;
    if (!force && Date.now() - lastCheckedAt.current < MIN_CHECK_GAP_MS) return;
    lastCheckedAt.current = Date.now();

    try {
      if (await isNewBuildAvailable()) setOpen(true);
    } catch (error) {
      // Offline / transient failure — try again on the next trigger.
    }
  }, []);

  useEffect(() => {
    // `yarn dev` has no hashed main bundle and no asset-manifest.json.
    if (process.env.NODE_ENV !== "production") return undefined;

    const onVisibilityChange = () => check();
    const onPageShow = (event) => check({ force: !!event.persisted });

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    const interval = setInterval(check, POLL_INTERVAL_MS);
    check({ force: true });

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      clearInterval(interval);
    };
  }, [check]);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Slide direction="down" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        role="alert"
        aria-live="assertive"
        sx={{
          position: "fixed",
          zIndex: (theme) => theme.zIndex.snackbar,
          left: 8,
          right: 8,
          top: "calc(8px + env(safe-area-inset-top, 0px))",
          p: 1.5,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
            {t("appUpdate.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("appUpdate.body")}
          </Typography>
        </Box>

        <Button variant="contained" size="small" onClick={handleReload} sx={{ flexShrink: 0 }}>
          {t("appUpdate.action")}
        </Button>

        <IconButton
          size="small"
          onClick={() => setOpen(false)}
          aria-label={t("Close")}
          sx={{ flexShrink: 0 }}
        >
          <X size={18} />
        </IconButton>
      </Paper>
    </Slide>
  );
};

export default AppUpdatePrompt;
